import { useAuthStore } from '@/stores/auth.store'

export interface TermsStatus {
  current_version: string
  accepted: boolean
  accepted_version: string | null
  accepted_at: string | null
  pdf_url: string
}

export interface TermsDocument {
  version: string
  pdf_url: string
  text: string
  required_checkboxes: string[]
}

export interface AcceptTermsResponse {
  accepted: boolean
  accepted_at: string
  version: string
}

const buildHeaders = (extra: Record<string, string> = {}): Record<string, string> => {
  const t = useAuthStore.getState().token
  const h: Record<string, string> = { ...extra }
  if (t) h.Authorization = `Bearer ${t}`
  return h
}

export const legalService = {
  async getStatus(): Promise<TermsStatus> {
    const r = await fetch('/api/v1/legal/terms-status', {
      headers: buildHeaders(),
    })
    if (!r.ok) throw new Error(`terms-status ${r.status}`)
    return r.json()
  },

  async getDocument(): Promise<TermsDocument> {
    const r = await fetch('/api/v1/legal/terms-document', {
      headers: buildHeaders(),
    })
    if (!r.ok) throw new Error(`terms-document ${r.status}`)
    return r.json()
  },

  async accept(version: string, checkboxes: Record<string, boolean>): Promise<AcceptTermsResponse> {
    const r = await fetch('/api/v1/legal/accept-terms', {
      method: 'POST',
      headers: buildHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify({ version, checkboxes }),
    })
    if (!r.ok) {
      const txt = await r.text()
      throw new Error(`accept-terms ${r.status}: ${txt}`)
    }
    return r.json()
  },

  async getAdminTermsAcceptance(params?: {
    status?: 'all' | 'accepted_current' | 'accepted_outdated' | 'never_accepted' | 'admin_exempt'
    search?: string
  }): Promise<AdminTermsAcceptanceResponse> {
    const qs = new URLSearchParams()
    if (params?.status && params.status !== 'all') qs.set('status', params.status)
    if (params?.search) qs.set('search', params.search)
    const url = `/api/v1/admin/terms-acceptance${qs.toString() ? `?${qs}` : ''}`
    const r = await fetch(url, { headers: buildHeaders() })
    if (!r.ok) throw new Error(`admin/terms-acceptance ${r.status}`)
    return r.json()
  },
}

export interface AdminTermsUserRow {
  id: number
  email: string
  full_name: string | null
  is_admin: boolean
  terms_status: 'accepted_current' | 'accepted_outdated' | 'never_accepted' | 'admin_exempt'
  terms_version_accepted: string | null
  terms_accepted_at: string | null
  terms_accepted_ip: string | null
  terms_accepted_user_agent: string | null
  created_at: string | null
}

export interface AdminTermsAcceptanceStats {
  total: number
  accepted_current: number
  accepted_outdated: number
  never_accepted: number
  admins: number
}

export interface AdminTermsAcceptanceResponse {
  current_version: string
  stats: AdminTermsAcceptanceStats
  users: AdminTermsUserRow[]
}
